import { useEffect, useState } from 'react';
import { AlertCircle, Building2, UserRound } from 'lucide-react';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { ImportPreviewBanner } from '@/components/admin/ImportPreviewBanner';
import {
  usePlatformOperators,
  useCreatePlatformProperty,
} from '@/hooks/api/usePlatform';
import type {
  PropertyImportPreview,
  CreatePlatformPropertyRequest,
} from '@/services/api/platform.api';
import { cn } from '@/lib/utils';

/** Origin details when the form is opened prefilled from an owner lead. */
export interface SubmissionMeta {
  ownerName: string;
  email: string;
  phone: string;
}

interface AddPropertyFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** Prefill parsed from an uploaded onboarding .xlsx (Import from Excel). */
  prefill?: PropertyImportPreview | null;
  /** Filename of the imported sheet, shown in the summary banner. */
  importFilename?: string | null;
  /**
   * Set when the prefill came from an owner lead ("From a submission"). Shows a
   * small origin banner so the admin knows where the details came from. Linking
   * is prefill-only — it does NOT change the lead's status.
   */
  submissionMeta?: SubmissionMeta | null;
}

interface AxiosLikeError {
  response?: { data?: { message?: string | string[] } };
}

function extractError(err: unknown): string {
  const msg = (err as AxiosLikeError)?.response?.data?.message;
  if (Array.isArray(msg)) return msg[0] ?? 'Failed to create property.';
  if (typeof msg === 'string') return msg;
  return 'Failed to create property. Please try again.';
}

const inputClass = cn(
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800',
  'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
);

export function AddPropertyForm({
  isOpen,
  onClose,
  prefill = null,
  importFilename = null,
  submissionMeta = null,
}: AddPropertyFormProps) {
  const { data: operators, isLoading: operatorsLoading } = usePlatformOperators();
  const createMutation = useCreatePlatformProperty();

  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Rich imported fields (amenities/times/policies) that have no input on this
  // minimal form — carried straight into the create payload so ONE import →
  // review → Create yields a fully-populated draft, not just the basics.
  const [importedRich, setImportedRich] = useState<Partial<CreatePlatformPropertyRequest>>({});

  // Apply an imported prefill: the mapped basics fill the visible inputs; the
  // rich fields (check-in/out, amenities, pet policy, house rules) are held and
  // sent with the create so the property lands fully filled. Everything is still
  // shown in the banner for review, and remains editable in the editor tabs.
  // BestAcco manages properties centrally → typically a single operator. Auto-
  // select it so the admin never sees a confusing "select operator" step.
  useEffect(() => {
    if (operators && operators.length === 1 && !tenantId) {
      setTenantId(operators[0]!.tenantId);
    }
  }, [operators, tenantId]);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.name !== undefined) setName(prefill.name);
    if (prefill.city !== undefined) setCity(prefill.city);
    if (prefill.address !== undefined) setAddress(prefill.address);
    if (prefill.country !== undefined) setCountry(prefill.country);
    if (prefill.description !== undefined) setDescription(prefill.description);
    setImportedRich({
      ...(prefill.amenities ? { amenities: prefill.amenities } : {}),
      ...(prefill.checkInTime ? { checkInTime: prefill.checkInTime } : {}),
      ...(prefill.checkOutTime ? { checkOutTime: prefill.checkOutTime } : {}),
      ...(prefill.petPolicy ? { petPolicy: prefill.petPolicy } : {}),
      ...(prefill.website ? { website: prefill.website } : {}),
      ...(prefill.zipCode ? { zipCode: prefill.zipCode } : {}),
      ...(prefill.houseRules ? { houseRules: prefill.houseRules } : {}),
    });
    setBannerDismissed(false);
  }, [prefill]);

  function reset() {
    setTenantId('');
    setName('');
    setCity('');
    setAddress('');
    setCountry('');
    setDescription('');
    setCurrency('USD');
    setImportedRich({});
    setError('');
    setBannerDismissed(false);
  }

  function handleClose() {
    if (createMutation.isPending) return;
    reset();
    onClose();
  }

  const trimmedCurrency = currency.trim().toUpperCase();
  const isValid =
    tenantId !== '' &&
    name.trim() !== '' &&
    city.trim() !== '' &&
    address.trim() !== '' &&
    country.trim() !== '' &&
    (trimmedCurrency === '' || trimmedCurrency.length === 3);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!isValid) {
      setError('Please fill in the operator, name, city, address and country.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        tenantId,
        name: name.trim(),
        city: city.trim(),
        address: address.trim(),
        country: country.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(trimmedCurrency ? { currency: trimmedCurrency } : {}),
        // Rich imported fields (empty {} for a hand-created property).
        ...importedRich,
      });
      reset();
      onClose();
    } catch (err) {
      setError(extractError(err));
    }
  }

  return (
    <SlideOver isOpen={isOpen} onClose={handleClose} title="Add Property">
      <form onSubmit={handleSubmit} className="space-y-4">
        {submissionMeta && (
          <div
            className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5 flex items-start gap-2"
            role="status"
          >
            <UserRound size={16} className="mt-0.5 shrink-0 text-brand-600" aria-hidden="true" />
            <p className="min-w-0 text-sm text-brand-800">
              Linked from submission by{' '}
              <span className="font-semibold">{submissionMeta.ownerName}</span>
              <span className="break-all">
                {submissionMeta.email ? ` · ${submissionMeta.email}` : ''}
                {submissionMeta.phone ? ` · ${submissionMeta.phone}` : ''}
              </span>
            </p>
          </div>
        )}

        {prefill && importFilename && !bannerDismissed && (
          <ImportPreviewBanner
            preview={prefill}
            filename={importFilename}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}

        <p className="text-sm text-gray-500">
          Create a listing on behalf of an operator. It is published as approved
          immediately and appears in that operator's account.
        </p>

        {error && (
          <div
            className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2"
            role="alert"
          >
            <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Operator picker — hidden when there's a single operator (the common
            BestAcco case: it's auto-selected). Shown only if multiple exist. */}
        {operators && operators.length > 1 && (
          <div>
            <label htmlFor="add-prop-operator" className="block text-sm font-medium text-gray-700 mb-1">
              Operator <span className="text-red-500">*</span>
            </label>
            <select
              id="add-prop-operator"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className={inputClass}
            >
              <option value="">Select an operator…</option>
              {operators.map((op) => (
                <option key={op.tenantId} value={op.tenantId}>
                  {op.name} ({op.propertyCount}{' '}
                  {op.propertyCount === 1 ? 'property' : 'properties'})
                </option>
              ))}
            </select>
          </div>
        )}
        {!operatorsLoading && operators && operators.length === 0 && (
          <p className="text-xs text-amber-600">
            No operator exists yet — one is needed before a property can be added.
          </p>
        )}

        <div>
          <label htmlFor="add-prop-name" className="block text-sm font-medium text-gray-700 mb-1">
            Property name <span className="text-red-500">*</span>
          </label>
          <input
            id="add-prop-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Seaside Boutique Hotel"
            maxLength={255}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="add-prop-address" className="block text-sm font-medium text-gray-700 mb-1">
            Address <span className="text-red-500">*</span>
          </label>
          <input
            id="add-prop-address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street address"
            maxLength={500}
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="add-prop-city" className="block text-sm font-medium text-gray-700 mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <input
              id="add-prop-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={100}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="add-prop-country" className="block text-sm font-medium text-gray-700 mb-1">
              Country <span className="text-red-500">*</span>
            </label>
            <input
              id="add-prop-country"
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              maxLength={100}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="add-prop-currency" className="block text-sm font-medium text-gray-700 mb-1">
            Currency
          </label>
          <input
            id="add-prop-currency"
            type="text"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            placeholder="USD"
            maxLength={3}
            className={cn(inputClass, 'uppercase max-w-[120px]')}
          />
          <p className="mt-1 text-xs text-gray-400">3-letter ISO code (default USD).</p>
        </div>

        <div>
          <label htmlFor="add-prop-desc" className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="add-prop-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={5000}
            className={inputClass}
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            type="submit"
            isLoading={createMutation.isPending}
            disabled={!isValid || createMutation.isPending}
          >
            <Building2 size={16} aria-hidden="true" />
            Create Property
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
